import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Claim, Project, Organization } from "@/types";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { formatDate } from "@/lib/utils";

interface ClaimEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claim: Claim;
  project?: Project;
  organization?: Organization;
  emailType: "acknowledgment" | "information-request" | "review-complete" | "approved" | "rejected";
  onSent: (newStatus?: string) => void;
}

interface EmailFormState {
  to: string;
  cc: string;
  subject: string;
  body: string;
}

// Predefined email templates
const emailTemplates = {
  acknowledgment: {
    subject: "Claim Acknowledgment: [CLAIM_ID]",
    body: `Dear [ORGANIZATION],

We are writing to acknowledge receipt of your claim [CLAIM_ID] for project [PROJECT_NAME], received on [DATE_RECEIVED].

Your claim is currently under review, and we will provide a response within the contractually required timeframe.

If you have any questions or need to provide additional information, please contact us.

Regards,
[YOUR_NAME]
[YOUR_ORGANIZATION]`
  },
  "information-request": {
    subject: "Additional Information Required: Claim [CLAIM_ID]",
    body: `Dear [ORGANIZATION],

Regarding your claim [CLAIM_ID] for project [PROJECT_NAME], we require additional information to proceed with our review.

Specifically, we need:
- [DETAIL_1]
- [DETAIL_2]
- [DETAIL_3]

Please provide this information at your earliest convenience. The review timeline will be adjusted accordingly.

Thank you for your cooperation.

Regards,
[YOUR_NAME]
[YOUR_ORGANIZATION]`
  },
  "review-complete": {
    subject: "Review Complete: Claim [CLAIM_ID]",
    body: `Dear [ORGANIZATION],

We have completed our review of claim [CLAIM_ID] for project [PROJECT_NAME].

Based on our review:
- [FINDING_1]
- [FINDING_2]
- [FINDING_3]

Next steps: [NEXT_STEPS]

If you have any questions, please contact us.

Regards,
[YOUR_NAME]
[YOUR_ORGANIZATION]`
  },
  approved: {
    subject: "Claim Approved: [CLAIM_ID]",
    body: `Dear [ORGANIZATION],

We are pleased to inform you that your claim [CLAIM_ID] for project [PROJECT_NAME] has been approved.

Approved amount: [AMOUNT]
Payment will be processed according to the contract terms.

If you have any questions, please contact us.

Regards,
[YOUR_NAME]
[YOUR_ORGANIZATION]`
  },
  rejected: {
    subject: "Claim Response: [CLAIM_ID]",
    body: `Dear [ORGANIZATION],

After careful review of claim [CLAIM_ID] for project [PROJECT_NAME], we regret to inform you that the claim has been rejected for the following reasons:

- [REASON_1]
- [REASON_2]
- [REASON_3]

If you wish to discuss this decision or provide additional information that might affect our determination, please contact us.

Regards,
[YOUR_NAME]
[YOUR_ORGANIZATION]`
  }
};

export default function ClaimEmailDialog({ 
  open, 
  onOpenChange,
  claim,
  project,
  organization,
  emailType,
  onSent
}: ClaimEmailDialogProps) {
  const { toast } = useToast();
  
  // Get template for the selected email type
  const template = emailTemplates[emailType];
  
  // Replace template variables with actual values
  const populateTemplate = (text: string) => {
    return text
      .replace(/\[CLAIM_ID\]/g, claim.claimIdentifier || claim.claimId.substring(0, 8))
      .replace(/\[PROJECT_NAME\]/g, project?.projectName || "")
      .replace(/\[ORGANIZATION\]/g, organization?.name || "Contractor")
      .replace(/\[DATE_RECEIVED\]/g, formatDate(claim.createdAt))
      .replace(/\[AMOUNT\]/g, `${claim.claimAmount} ${claim.claimAmountCurrency}`);
  };
  
  // Form state
  const [formState, setFormState] = useState({
    to: organization?.contactEmail || organization?.primaryContactEmail || "",
    cc: "",
    subject: populateTemplate(template.subject),
    body: populateTemplate(template.body)
  });
  
  // Update form state
  const handleChange = (field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (data: EmailFormState) => {
      // Create a communication log entry
      const logData = {
        claimId: claim.claimId,
        logType: "Email Sent",
        subject: data.subject,
        body: data.body,
        recipients: data.to + (data.cc ? `, ${data.cc}` : "")
      };
      
      // POST to create a communication log
      const res = await apiRequest("POST", `/api/claims/${claim.claimId}/communicationLogs`, logData);
      return res.json();
    },
    onSuccess: () => {
      // Email sent successfully - status changes now happen through proper workflow
      // No automatic status changes from email sending
      
      // Just invalidate queries and show success - no automatic status changes
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Email record created",
        description: "The email has been recorded in the communication log",
      });
      
      onOpenChange(false);
      onSent();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send email",
        variant: "destructive",
      });
    },
  });
  
  // Update claim status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const res = await apiRequest("PUT", `/api/claims/${claim.claimId}`, { status });
      return res.json();
    },
    onSuccess: (_, status) => {
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Email sent and status updated",
        description: `The communication has been logged and claim status updated to ${status}`,
      });
      
      onOpenChange(false);
      onSent(status);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Email logged but failed to update claim status",
        variant: "destructive",
      });
      
      onOpenChange(false);
    },
  });
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formState.to || !formState.subject || !formState.body) {
      toast({
        title: "Validation error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    // Submit form
    sendEmailMutation.mutate(formState);
  };
  
  // Get dialog title based on email type
  const getDialogTitle = () => {
    switch (emailType) {
      case "acknowledgment":
        return "Send Acknowledgment Email";
      case "information-request":
        return "Request Additional Information";
      case "review-complete":
        return "Send Review Results";
      case "approved":
        return "Send Approval Notification";
      case "rejected":
        return "Send Rejection Notification";
      default:
        return "Send Email";
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>
            Record an email communication with the contractor.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="to" className="text-right">
                To
              </Label>
              <Input
                id="to"
                value={formState.to}
                onChange={(e) => handleChange("to", e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="cc" className="text-right">
                CC
              </Label>
              <Input
                id="cc"
                value={formState.cc}
                onChange={(e) => handleChange("cc", e.target.value)}
                className="col-span-3"
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subject" className="text-right">
                Subject
              </Label>
              <Input
                id="subject"
                value={formState.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="body" className="text-right pt-2">
                Message
              </Label>
              <Textarea
                id="body"
                rows={12}
                value={formState.body}
                onChange={(e) => handleChange("body", e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">
                Template
              </Label>
              <Select 
                onValueChange={(value) => {
                  const template = emailTemplates[value as keyof typeof emailTemplates];
                  handleChange("subject", populateTemplate(template.subject));
                  handleChange("body", populateTemplate(template.body));
                }}
                defaultValue={emailType}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acknowledgment">Acknowledgment</SelectItem>
                  <SelectItem value="information-request">Information Request</SelectItem>
                  <SelectItem value="review-complete">Review Complete</SelectItem>
                  <SelectItem value="approved">Approval</SelectItem>
                  <SelectItem value="rejected">Rejection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={sendEmailMutation.isPending || updateStatusMutation.isPending}
            >
              {sendEmailMutation.isPending || updateStatusMutation.isPending ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}